/**
 * `report-metrics` v1 — a collection of metrics from one periodically
 * refreshed report.
 *
 * Every metric carries its own named period and comparison, so one request can
 * feed KPI tiles that measure different periods (yesterday, last 7 days…).
 * Instances sharing a report source and tenant scope share one deduplicated
 * request; each tile selects its metric with `config.metricId`.
 */
export const REPORT_METRICS_CONTRACT = { name: "report-metrics", version: 1 } as const;

/**
 * - `number`: a count, shown without decimals.
 * - `decimal`: a quantity, shown with one decimal.
 * - `percent`: a ratio where `1` is 100 %. `deltaPercent` is always percentage
 *   points of change, whatever the format.
 */
export type ReportMetricFormat = "number" | "percent" | "decimal";

export interface ReportPeriod {
  /** Named period: today | yesterday | last_7_days | last_14_days | recent_months | selected. */
  key: string;
  /** ISO 8601 UTC instants, computed by the backend in tenant-local calendar days. */
  from: string;
  to: string;
  /** Human label owned by the backend, e.g. "Yesterday". */
  label: string;
}

export interface ReportFreshness {
  generatedAt: string;
  /** How far the underlying data goes; reports are snapshots, not real-time views. */
  dataThrough: string;
  /** "demo", "clickhouse", or a named upstream feed. */
  source: string;
  isEstimated: boolean;
}

export interface ReportMetric {
  id: string;
  label: string;
  /** `null` marks a metric the source could not compute. */
  value: number | null;
  format: ReportMetricFormat;
  unit?: string | null;
  period: ReportPeriod | null;
  comparisonValue?: number | null;
  deltaPercent?: number | null;
  occurredAt?: string | null;
  /** Share of the period the value is computed from, 0–1; omitted when complete or unknown. */
  coverage?: number | null;
  /**
   * Optional history of the metric, oldest first (e.g. one value per month),
   * drawn as a sparkline by widgets that show one. Live sources leave it out.
   */
  series?: number[] | null;
}

/** What a metrics collection measures, e.g. one campaign; used as a page heading. */
export interface ReportMetricsSubject {
  title: string;
  subtitle?: string | null;
  /** Image reference (see `WidgetAssetsContext`), e.g. the tenant's logo. */
  image?: string | null;
}

export interface ReportMetricsData {
  metrics: ReportMetric[];
  /** `null` for live sources that have no snapshot semantics. */
  freshness: ReportFreshness | null;
  /** Optional: set by report snapshots whose collection describes one entity. */
  subject?: ReportMetricsSubject | null;
}
